import { modelOptions, PropType, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import type { DataTypeSpecifier } from "../../../../HistoricalData.js";
import {
    DataTypeSpecifierString,
    getZodSchemaFromClass,
    SproutCheckTypeString,
    validate,
    VerificationStatusString,
    type SproutCheckType,
    type VerificationStatus,
} from "../../../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../../schemaBase.js";
import type {
    GeneralSproutVerificationCheckFieldsWithDefaults,
    GeneralSproutVerificationCheckSchemaTypes,
} from "../../../types/generalSproutVerificationCheck.d.js";
import type { DtoValidatorTypes } from "../../../validatableClass.js";

const SCHEMA_KEY = "GeneralSproutVerificationCheck" as const;

export type { SproutCheckType, VerificationStatus } from "../../../../zod-decorator/index.js";

// Forward declaration to avoid circular dependencies
type AcceptableValueRange = any;

/** Numeric severity codes for each verification status, used for sorting and alerting */
export const VerificationCodeByStatus = {
    successful: 0, // Verification passed completely
    pending: 5, // Waiting for additional data (temporary status)
    inconclusive: 7, // Insufficient data to determine success/failure
    warning: 10, // Problem detected but irrigation can still proceed
    error: 15, // Irrigation likely failed or had substantial issues
    catastrophic: 20, // Severe failure affecting multiple irrigations (unused currently)
} as const satisfies Record<VerificationStatus, number>;

export const VerificationStatusByCode: Record<number, VerificationStatus> = {};
Object.keys(VerificationCodeByStatus).forEach((status: VerificationStatus) => {
    VerificationStatusByCode[VerificationCodeByStatus[status]] = status;
});

/**
 * Individual verification check result for a specific system component.
 *
 * This model represents the outcome of testing one aspect of the irrigation system
 * (valve state, sensor readings, connectivity, timing, or solenoid operation).
 *
 * Each check provides:
 * - A status indicating success/failure/pending state
 * - The actual value measured during verification
 * - The acceptable range or expected value for comparison
 * - Human-readable explanation of the result
 * - Timing information for when data was collected
 *
 * Status hierarchy (by severity):
 * - successful (0): Everything working as expected
 * - pending (5): Waiting for data, temporary state
 * - inconclusive (7): Not enough data to determine outcome
 * - warning (10): Issue detected but irrigation can proceed
 * - error (15): Likely irrigation failure or significant problem
 * - catastrophic (20): Severe system failure (currently unused)
 */
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<GeneralSproutVerificationCheck>>(...addSchemaKeyHook(SCHEMA_KEY))
export class GeneralSproutVerificationCheck extends SchemaBase<
    GeneralSproutVerificationCheck,
    GeneralSproutVerificationCheckFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    /** Type of verification check performed (valveState, sensor, online, etc.) */
    @validate({ zodValidator: SproutCheckTypeString })
    @prop({ type: () => String, required: true })
    public checkType!: SproutCheckType;

    /** Result status of this verification check */
    @validate({ zodValidator: VerificationStatusString })
    @prop({ type: () => String, required: true })
    public status!: VerificationStatus;

    /** Human-readable explanation of the verification result */
    @validate({ zodValidator: z.string() })
    @prop({ type: () => String, required: true })
    public statusReason!: string;

    /** Expected value range or exact value for this check */
    @validate({ zodValidator: z.any().optional() })
    @prop({ type: () => Object, required: false })
    public acceptableRange?: AcceptableValueRange;

    /** Actual measured value during verification */
    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public value?: number;

    /** Data type specification for the measured value */
    @validate({ zodValidator: DataTypeSpecifierString.optional() })
    @prop({ type: () => String, required: false })
    public valueDataType?: DataTypeSpecifier;

    /** Timestamp when the measured value was collected */
    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: false })
    public valueGatheredAt?: Date;

    /** Additional explanatory notes about this verification check */
    @validate({ zodValidator: z.array(z.string()).optional() })
    @prop({ type: () => String, required: false }, PropType.ARRAY)
    public notes?: string[];

    static getters = {} as const satisfies Record<string, (self: GeneralSproutVerificationCheck) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<GeneralSproutVerificationCheckSchemaTypes>;

    constructor(
        properties?: SchemaConstructorArgs<
            GeneralSproutVerificationCheck,
            GeneralSproutVerificationCheckFieldsWithDefaults
        >,
    ) {
        super(properties);
        Object.assign(this, properties);
    }
}

GeneralSproutVerificationCheck.dtoValidators = getZodSchemaFromClass(
    GeneralSproutVerificationCheck,
) as unknown as DtoValidatorTypes<GeneralSproutVerificationCheckSchemaTypes>;
